import { Either, left, right } from "fp-ts/lib/Either";
import ExceptionEntity from "../../../../../domain/entities/ExceptionEntity";
import di from "../../../../../di/DependencyInjection";
import UserHostApi, { UserHostApiName } from "../../../../settings/UserHostApi";

/**
 * Moves a user to a different bucket by updating their client/business_unit.
 * 
 * PUT /usersetup requirements:
 * - email: user's email (required)
 * - role: existing role_name (e.g. "chief_", "supervisor", "agent_")
 * - position: existing position or "" 
 * - business_unit: target business unit name (e.g. "MIA Aesthetics", "JPS", "Anyone Home")
 * - timezone: valid timezone string (e.g. "America/Jamaica", "America/Bogota")
 * - location: one of: Belize, Jamaica, Colombia, Guyana, Saint Lucia, US, Honduras, Training
 * - client: target business unit name (same as business_unit)
 * - supervisor: supervisor email or ""
 * - hr_id: existing hr_id or ""
 */
const MoveUserToBucketApiImpl = async (userEmail: string, location: string, client: string, supervisor: string): Promise<Either<ExceptionEntity, string>> => {
    try {
        // Fetch user's current data to preserve required fields
        const usersResponse = await di.get<UserHostApi>(UserHostApiName).get(`/users?page=1&per_page=50&keyword=`);
        const usersData = usersResponse.data ?? usersResponse;
        const currentUser = (Array.isArray(usersData) ? usersData : []).find(
            (u: any) => u.email?.toLowerCase() === userEmail.toLowerCase()
        );

        if (!currentUser) {
            return left({ message: "User not found" });
        }

        // Pass the target bucket's name as the new client/business_unit
        // Preserve all other fields (role, timezone, location, position, hr_id) from current user
        const payload = [{
            email: userEmail,
            role: currentUser.role_name ?? "",
            position: currentUser.position ?? "",
            business_unit: client,
            timezone: getTimezoneString(currentUser.timezone_id),
            location: currentUser.location ?? getLocationFromTimezone(currentUser.timezone_id),
            client: client,
            supervisor: supervisor || "",
            hr_id: currentUser.hr_id ?? ""
        }];

        const response = await di.get<UserHostApi>(UserHostApiName).put("/usersetup", payload);
        return right(response?.message ?? "User moved successfully");
    } catch (error: any) {
        return left({ message: error?.message ?? "Error moving user" });
    }
};

const getTimezoneString = (timezoneId: number): string => {
    const map: { [key: number]: string } = {
        1: "America/Jamaica",
        2: "America/Bogota",
        3: "America/Belize",
        4: "US/Eastern",
        5: "US/Central",
        6: "US/Mountain",
        7: "US/Pacific",
        8: "America/Tegucigalpa",
        9: "America/Guyana",
        10: "America/St_Lucia",
    };
    return map[timezoneId] ?? "America/Jamaica";
};

// Fallback location based on timezone
const getLocationFromTimezone = (timezoneId: number): string => {
    const map: { [key: number]: string } = {
        1: "Jamaica",
        2: "Colombia",
        3: "Belize",
        4: "US",
        5: "US",
        6: "US",
        7: "US",
        8: "Honduras",
        9: "Guyana",
        10: "Saint Lucia",
    };
    return map[timezoneId] ?? "Jamaica";
};

export default MoveUserToBucketApiImpl;
